import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { convertFromUTC, convertToUTC } from '../../../util/convert_timezone';
import dateService from '../../../services/dateService';
import { isMultiLocation, isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

const TARGET_ROLE_LABELS = {
  owner: 'Owner',
  manager: 'Manager',
  technician: 'Technician',
  front_desk: 'Front Desk'
};

const TARGET_ROLE_OPTIONS = [
  { value: '', label: 'Everyone' },
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'technician', label: 'Technician' },
  { value: 'front_desk', label: 'Front Desk' }
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
];

const SNOOZE_OPTIONS = [
  { label: 'In 1 hour', hours: 1 },
  { label: 'In 4 hours', hours: 4 },
  { label: 'Tomorrow morning', tomorrow: true },
  { label: 'Next week', days: 7 }
];

const PRIORITY_COLORS = {
  urgent: '#DC2626',
  high: '#EA580C',
  normal: '#2563EB',
  low: '#6B7280'
};

// Linkify phone numbers in text
const PHONE_REGEX = /(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;

function renderWithPhoneLinks(text) {
  if (!text) return null;
  const parts = text.split(PHONE_REGEX);
  return parts.map((part, i) => {
    if (PHONE_REGEX.test(part)) {
      PHONE_REGEX.lastIndex = 0;
      const digits = part.replace(/[^\d+]/g, '');
      return (
        <a key={i} href={`tel:${digits}`} style={{ color: '#3B82F6', fontWeight: 600 }}>
          {part}
        </a>
      );
    }
    return part;
  });
}

class TaskDetail extends Component {
  state = {
    editing: false,
    editTitle: '',
    editNextAction: '',
    editDescription: '',
    editPriority: 'normal',
    editDueDate: null,
    editAssignedTo: '',
    editTargetRole: '',
    showSnoozeMenu: false,
    showDeleteConfirm: false
  };

  componentDidUpdate(prevProps) {
    if (prevProps.task?.task_id !== this.props.task?.task_id) {
      this.setState({ editing: false, showSnoozeMenu: false, showDeleteConfirm: false });
    }
  }

  startEditing = () => {
    const { task } = this.props;
    this.setState({
      editing: true,
      editTitle: task.title || '',
      editNextAction: task.next_action || '',
      editDescription: task.description || '',
      editPriority: task.priority || 'normal',
      editDueDate: task.due_date ? dateService.fromAPIDate(task.due_date).toDate() : null,
      editAssignedTo: task.assigned_to_user_id || task.assigned_to || '',
      editTargetRole: task.target_role || ''
    });
  };

  cancelEditing = () => {
    this.setState({ editing: false });
  };

  handleSave = () => {
    const { editTitle, editNextAction, editDescription, editPriority, editDueDate, editAssignedTo, editTargetRole } = this.state;
    this.props.onUpdate(this.props.task.task_id, {
      title: editTitle,
      next_action: editNextAction || null,
      description: editDescription,
      priority: editPriority,
      due_date: editDueDate ? moment(editDueDate).format('YYYY-MM-DD') : null,
      assigned_to: editAssignedTo || null,
      target_role: editTargetRole || null
    });
    this.setState({ editing: false });
  };

  handleSnooze = (option) => {
    let localTime;
    if (option.tomorrow) {
      localTime = moment().add(1, 'day').startOf('day').hour(8);
    } else if (option.days) {
      localTime = moment().add(option.days, 'days').startOf('day').hour(8);
    } else {
      localTime = moment().add(option.hours, 'hours');
    }
    const snoozeUntil = convertToUTC(localTime).format('YYYY-MM-DD HH:mm:ss');
    this.props.onSnooze(this.props.task.task_id, snoozeUntil);
    this.setState({ showSnoozeMenu: false });
  };

  handleEntityClick = () => {
    const { task } = this.props;
    if (task.entity_type === 'request') {
      this.props.history.push('/requestimates', { selectRequestId: task.entity_id });
    }
  };

  getStaffName(userId) {
    const { staffList } = this.props;
    if (!userId || !staffList) return null;
    const staff = staffList.find(s => String(s.User_id || s.user_id) === String(userId));
    return staff ? (staff.user_name || staff.name || `${staff.First_name || ''} ${staff.Last_name || ''}`.trim()) : null;
  }

  render() {
    const { task, onComplete, onDelete, user, staffList, locations, selectedLocationId } = this.props;
    const multiLoc = isMultiLocation(locations);
    const allLocView = isAllLocations(selectedLocationId);
    const { editing, showSnoozeMenu, showDeleteConfirm } = this.state;

    if (!task) return null;

    const localDueDate = task.due_date ? dateService.fromAPIDate(task.due_date) : null;
    const localCreatedAt = task.created_at ? convertFromUTC(task.created_at) : null;
    const localCompletedAt = task.completed_at ? convertFromUTC(task.completed_at) : null;
    const isOverdue = localDueDate && localDueDate.isBefore(moment(), 'day') && task.status !== 'completed';
    const isCompleted = task.status === 'completed';
    const assigneeName = task.assigned_to_name || this.getStaffName(task.assigned_to_user_id || task.assigned_to);
    const creatorName = task.created_by_name || this.getStaffName(task.created_by_user_id || task.created_by);
    const completedByName = task.completed_by_name || this.getStaffName(task.completed_by_user_id || task.completed_by);
    const priColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.normal;
    const loc = multiLoc && allLocView && task.location_id
      ? (locations || []).find(l => String(l.location_id) === String(task.location_id))
      : null;

    return (
      <React.Fragment>
        <div className="card text-center">
          <div className="mt-4 mb-4">

            {/* Title + priority badge inline — red if overdue */}
            {editing ? (
              <div style={{ maxWidth: '400px', margin: '0 auto 10px' }}>
                <label className="bold">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={this.state.editTitle}
                  onChange={(e) => this.setState({ editTitle: e.target.value })}
                  autoFocus
                />
              </div>
            ) : (
              <h5 className="mb-2" style={{ color: isOverdue ? '#DC2626' : undefined }}>
                {task.title}
                {' '}
                <span className="badge" style={{ backgroundColor: priColor, color: '#fff', fontSize: '0.65rem', verticalAlign: 'middle' }}>
                  {(task.priority || 'normal').toUpperCase()}
                </span>
                {task.target_role && (
                  <>
                    {' '}
                    <span className="badge" style={{ backgroundColor: '#E5E7EB', color: '#374151', fontSize: '0.65rem', verticalAlign: 'middle' }}>
                      <i className="fa-sharp fa-regular fa-user mr-1" style={{ fontSize: '0.55rem' }} />
                      {TARGET_ROLE_LABELS[task.target_role] || task.target_role}
                    </span>
                  </>
                )}
                {isCompleted && (
                  <span className="badge badge-success ml-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                    <i className="fa-sharp fa-solid fa-circle-check" /> Completed
                  </span>
                )}
              </h5>
            )}

            {/* Entity contact info — name, phone, email */}
            {!editing && (task.entity_name || task.entity_phone || task.entity_email) && (
              <div className="mb-3">
                {task.entity_name && (
                  <div><strong>{task.entity_name}</strong></div>
                )}
                {task.entity_phone && (
                  <div>
                    <a href={`tel:${task.entity_phone.replace(/[^\d+]/g, '')}`} style={{ color: '#3B82F6' }}>
                      <i className="fa-sharp fa-regular fa-phone mr-1" />{task.entity_phone}
                    </a>
                  </div>
                )}
                {task.entity_email && (
                  <div>
                    <a href={`mailto:${task.entity_email}`} style={{ color: '#3B82F6' }}>
                      <i className="fa-sharp fa-regular fa-envelope mr-1" />{task.entity_email}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Next Action — primary content */}
            {editing ? (
              <div style={{ maxWidth: '400px', margin: '0 auto 15px' }}>
                <label className="bold">Action</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={this.state.editNextAction}
                  onChange={(e) => this.setState({ editNextAction: e.target.value })}
                  placeholder="What to do — phone number, amount, details..."
                />
              </div>
            ) : task.next_action ? (
              <div className="mb-3 text-left" style={{
                backgroundColor: '#f8f9fa',
                borderRadius: 8,
                padding: '12px 16px',
                fontSize: '0.95rem',
                lineHeight: 1.5,
                maxWidth: '400px',
                margin: '0 auto 15px'
              }}>
                {renderWithPhoneLinks(task.next_action)}
              </div>
            ) : null}

            {/* Due Date */}
            {editing ? (
              <div style={{ maxWidth: '300px', margin: '0 auto 15px' }}>
                <label className="bold">Due Date</label>
                <DatePicker
                  selected={this.state.editDueDate}
                  onChange={(date) => this.setState({ editDueDate: date })}
                  dateFormat="MMM d, yyyy"
                  className="form-control"
                  placeholderText="Select due date..."
                  isClearable
                />
              </div>
            ) : localDueDate ? (
              <div className="mb-2">
                <h6 style={{ color: isOverdue ? '#DC2626' : undefined, fontWeight: isOverdue ? 600 : undefined }}>
                  <i className="fa-sharp fa-regular fa-calendar mr-1" />
                  {isOverdue ? 'Overdue: ' : 'Due: '}
                  {localDueDate.format('MMM D, YYYY')}
                </h6>
              </div>
            ) : null}

            {/* Assigned To */}
            {editing ? null : assigneeName ? (
              <div className="mb-2">
                <h6><i className="fa-sharp fa-regular fa-user mr-1" /> {assigneeName}</h6>
              </div>
            ) : null}

            {/* Priority, Who is this for?, Assign To (edit mode) */}
            {editing && (
              <>
                <div className="row" style={{ maxWidth: '400px', margin: '0 auto 15px' }}>
                  <div className="col-6">
                    <label className="bold">Priority</label>
                    <Form.Control
                      as="select"
                      value={this.state.editPriority}
                      onChange={(e) => this.setState({ editPriority: e.target.value })}
                    >
                      {PRIORITY_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </Form.Control>
                  </div>
                  <div className="col-6">
                    <label className="bold">Who is this for?</label>
                    <Form.Control
                      as="select"
                      value={this.state.editTargetRole}
                      onChange={(e) => this.setState({ editTargetRole: e.target.value, editAssignedTo: '' })}
                    >
                      {TARGET_ROLE_OPTIONS.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </Form.Control>
                  </div>
                </div>
                <div style={{ maxWidth: '300px', margin: '0 auto 15px' }}>
                  <label className="bold">Assign To</label>
                  <Form.Control
                    as="select"
                    value={this.state.editAssignedTo}
                    onChange={(e) => this.setState({ editAssignedTo: e.target.value })}
                  >
                    <option value="">All</option>
                    {(staffList || [])
                      .filter(s => {
                        if (!this.state.editTargetRole) return true;
                        const r = (s.role || '').toLowerCase();
                        if (this.state.editTargetRole === 'front_desk') return r !== 'technician' && r !== 'owner';
                        return r === this.state.editTargetRole;
                      })
                      .map(s => (
                        <option key={s.User_id || s.user_id} value={s.User_id || s.user_id}>
                          {s.user_name || s.name || `${s.First_name || ''} ${s.Last_name || ''}`.trim()}
                        </option>
                      ))}
                  </Form.Control>
                </div>
              </>
            )}

            {/* Location badge */}
            {loc && (
              <div className="mb-2">
                <span className="badge" style={{
                  backgroundColor: getLocationBadgeColor(task.location_id, locations),
                  color: '#fff'
                }}>
                  {getLocationName(loc)}
                </span>
              </div>
            )}

            {/* Entity Link — only for requests (navigates to job) */}
            {task.entity_type === 'request' && task.entity_id && (
              <div className="mb-3">
                <span
                  style={{ color: '#3B82F6', cursor: 'pointer' }}
                  onClick={this.handleEntityClick}
                >
                  <i className="fa-sharp fa-regular fa-link mr-1" />
                  Job #{task.entity_id}
                </span>
              </div>
            )}

            {/* Action buttons */}
            {!isCompleted && !editing && (
              <div className="d-flex justify-content-center flex-nowrap">
                <button
                  type="button"
                  className="btn btn-success m-1 buttons"
                  onClick={() => onComplete(task.task_id, user.User_id)}
                >
                  <i className="fa-sharp fa-solid fa-check" /> COMPLETE
                </button>
                <div className="task-snooze-dropdown">
                  <button
                    type="button"
                    className="btn btn-warning m-1 buttons"
                    style={{ color: '#fff' }}
                    onClick={() => this.setState({ showSnoozeMenu: !showSnoozeMenu })}
                  >
                    <i className="fa-sharp fa-regular fa-clock" /> SNOOZE
                  </button>
                  {showSnoozeMenu && (
                    <div className="task-snooze-menu">
                      {SNOOZE_OPTIONS.map((opt, i) => (
                        <button
                          key={i}
                          className="task-snooze-option"
                          onClick={() => this.handleSnooze(opt)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary m-1 buttons"
                  onClick={this.startEditing}
                >
                  <i className="fa-sharp fa-regular fa-pen" /> EDIT
                </button>
              </div>
            )}

            {/* Edit mode: context/description field */}
            {editing && (
              <div style={{ maxWidth: '400px', margin: '0 auto 15px' }}>
                <label className="bold">Context</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={this.state.editDescription}
                  onChange={(e) => this.setState({ editDescription: e.target.value })}
                  placeholder="Background notes (optional)"
                />
              </div>
            )}

            {/* Edit mode buttons */}
            {editing && (
              <div className="d-flex justify-content-center flex-nowrap">
                <button type="button" className="btn btn-success m-1 buttons" onClick={this.handleSave}>
                  SAVE
                </button>
                <button type="button" className="button m-1 buttons" onClick={this.cancelEditing}>
                  CANCEL
                </button>
              </div>
            )}

            {/* Context / Description — muted, below actions */}
            {!editing && task.description && (
              <div className="mt-3 text-left" style={{
                borderTop: '1px solid #dee2e6',
                paddingTop: 12,
                maxWidth: '400px',
                margin: '12px auto 0'
              }}>
                <small className="text-muted" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Context
                </small>
                <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  {task.description}
                </p>
              </div>
            )}

            {/* Timestamps */}
            <div className="mt-3 mb-1">
              <small className="text-muted">
                Created {localCreatedAt ? localCreatedAt.format('MMM D, YYYY h:mm A') : ''}
                {creatorName && ` by ${creatorName}`}
                {isCompleted && localCompletedAt && (
                  <>
                    {' '}&middot; Completed {localCompletedAt.format('MMM D, YYYY h:mm A')}
                    {completedByName && ` by ${completedByName}`}
                  </>
                )}
              </small>
            </div>

            {/* Delete — only for non-completed, non-edit */}
            {!isCompleted && !editing && (
              <div className="mt-3">
                {showDeleteConfirm ? (
                  <div>
                    <small className="text-danger mr-2">Delete this reminder?</small>
                    <button type="button" className="btn btn-danger m-1" onClick={() => onDelete(task.task_id)}>
                      Yes, delete
                    </button>
                    <button type="button" className="btn btn-outline-secondary m-1" onClick={() => this.setState({ showDeleteConfirm: false })}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-danger m-1"
                    onClick={() => this.setState({ showDeleteConfirm: true })}
                  >
                    <i className="fa-sharp fa-regular fa-trash" /> DELETE
                  </button>
                )}
              </div>
            )}

          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(TaskDetail);
